import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { X } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { DetailCashRegister } from '../../interfaces/detail-cash-register';
import { HttpExceptionDto } from '../../interfaces/http-exception.dto';
import { UpdateClosedCashRegisterCheckDto } from '../../interfaces/update-closed-cash-register-check.dto';
import { api } from '../../services/api';
import myToastr from '../../services/toastr';
import Input from '../Input/Input';
import Textarea from '../Textarea/Textarea';
import styles from './ClosedCashRegisterModal.module.css';

interface Props {
  closeModal: (result: boolean) => void;
  id: number;
  show: boolean;
}

const initialValues: UpdateClosedCashRegisterCheckDto = {
  openingCash: 0,
  closingCash: 0,
  closingCards: 0,
  closingWithdrawals: 0,
  notes: '',
};

export const ClosedCashRegisterModal = ({ closeModal, id, show }: Props) => {
  const [detailCashRegister, setDetailCashRegister] = useState<DetailCashRegister | null>(null);
  const {
    control,
    formState: { isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = useForm<UpdateClosedCashRegisterCheckDto>();

  const loadData = useCallback(async () => {
    try {
      const data = await api.getDetailCashRegisterCheck(id);
      setDetailCashRegister(data);
      reset({
        openingCash: data.cashRegisterCheck.openingCash,
        closingCash: data.cashRegisterCheck.closingCash,
        closingCards: data.cashRegisterCheck.closingCards,
        closingWithdrawals: data.cashRegisterCheck.closingWithdrawals,
        notes: data.cashRegisterCheck.closingNotes || '',
      });
    } catch (e: any) {
      const httpExceptionDto = e.response?.data as HttpExceptionDto;
      myToastr.error(httpExceptionDto.message);
    }
  }, [id, reset]);

  useEffect(() => {
    if (show && id) {
      loadData();
    }
  }, [show, id, loadData]);

  useEffect(() => {
    if (!show) {
      reset(initialValues);
    }
  }, [show, reset]);

  const handleClose = (result = false) => {
    reset();
    setDetailCashRegister(null);
    closeModal(result);
  };

  const onSubmit = async (data: UpdateClosedCashRegisterCheckDto) => {
    if (!isDirty) {
      handleClose();
      return;
    }
    try {
      await api.updateClosedCashRegisterCheck(id, data);
      myToastr.success('Arqueo de caja guardado correctamente');
      handleClose(true);
    } catch (e: any) {
      const httpExceptionDto = e.response?.data as HttpExceptionDto;
      myToastr.error(httpExceptionDto?.message || 'Error al guardar el arqueo de caja');
    }
  };

  return (
    <Modal className={clsx('vercomi-modal my-form', styles.container)} isOpen={show} onRequestClose={() => handleClose()} shouldCloseOnOverlayClick={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="content">
          <div className="d-flex justify-content-between mb-3 text-center">
            <div className="title flex-grow-1">Arqueo de caja {detailCashRegister?.cashRegisterCheck.internalId}</div>
            <button className="close-button-modal" onClick={() => handleClose()} title="Cerrar" type="button">
              <X size={16} />
            </button>
          </div>
          <div className="d-flex flex-column gap-3">
            <div className="row">
              <div className="col">
                <Controller
                  control={control}
                  name="openingCash"
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      errorMessage={error?.message}
                      label="Saldo de apertura"
                      min={0}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      required
                      step={0.01}
                      placeholder="0,00€"
                      type="number"
                    />
                  )}
                  rules={{
                    min: {
                      message: 'El valor no puede ser negativo',
                      value: 0,
                    },
                    required: 'Campo obligatorio',
                  }}
                />
              </div>
              <div className="col">
                <Controller
                  control={control}
                  name="closingCash"
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      errorMessage={error?.message}
                      label="Efectivo al cierre"
                      min={0}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      required
                      step={0.01}
                      placeholder="0,00€"
                      type="number"
                    />
                  )}
                  rules={{
                    min: {
                      message: 'El valor no puede ser negativo',
                      value: 0,
                    },
                    required: 'Campo obligatorio',
                  }}
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <Controller
                  control={control}
                  name="closingCards"
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      errorMessage={error?.message}
                      label="TPV al cierre"
                      min={0}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      required
                      step={0.01}
                      placeholder="0,00€"
                      type="number"
                    />
                  )}
                  rules={{
                    min: {
                      message: 'El valor no puede ser negativo',
                      value: 0,
                    },
                    required: 'Campo obligatorio',
                  }}
                />
              </div>
              <div className="col">
                <Controller
                  control={control}
                  name="closingWithdrawals"
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      errorMessage={error?.message}
                      label="Retiradas al cierre"
                      min={0}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      required
                      step={0.01}
                      placeholder="0,00€"
                      type="number"
                    />
                  )}
                  rules={{
                    min: {
                      message: 'El valor no puede ser negativo',
                      value: 0,
                    },
                    required: 'Campo obligatorio',
                  }}
                />
              </div>
            </div>
            <div className="row">
              <Controller control={control} name="notes" render={({ field }) => <Textarea {...field} label="Notas al cierre" rows={3} />} />
            </div>
            <div className="row px-3">
              <div className={styles.warning}>
                <strong>Nota:</strong> El saldo de cierre no corresponderá con el saldo de apertura del día siguiente.
              </div>
            </div>
          </div>
        </div>
        <button disabled={!Boolean(detailCashRegister) || isSubmitting} className="save-button" type="submit">
          Guardar
        </button>
      </form>
    </Modal>
  );
};
